import { Suspense, lazy, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { Flame, House, LogIn } from "lucide-react";

import { Button } from "@/components/ui/button";
import { LanguageMenuRow } from "@/components/settings/LanguageMenuRow";
import { ThemeMenuRow } from "@/components/settings/ThemeMenuRow";
import { useHydrated } from "@/hooks/useHydrated";
import { cn } from "@/engine/support/classNames";

/**
 * The session and the account menu, fetched after first paint: they bring the
 * Supabase client, which the marketing face does not carry before it.
 */
const LandingSession = lazy(() => import("@/components/entry/LandingSession"));

/**
 * The landing's header: the name, and the way back to an account.
 *
 * A guest is offered Sign in beside the two dials that matter before an
 * account — the language and the palette. Somebody already signed in gets
 * neither where a guest sees them: their own character stands in the middle
 * instead, and both dials wait for them one level down in the account menu.
 * The server cannot know the session, so Sign in stands until hydration and
 * until the session has been read.
 *
 * `showSignIn` closes that offer where the page already holds it: the sign-in
 * page is the way in drawn at full size, so a second one above it is a row
 * taken for nothing. The dials stay — they matter wherever the visitor stands.
 */
export function LandingHeader({ showSignIn = true }: { showSignIn?: boolean }) {
  const hydrated = useHydrated();
  const [signedIn, setSignedIn] = useState(false);

  // One bar from the server's paint to the session's answer: the session only
  // fills the account's place in it. Swapping the bar for another as each
  // arrived remounted it, and closed a dial a guest had already opened.
  const account = hydrated ? (
    <Suspense fallback={null}>
      <LandingSession onSignedIn={setSignedIn} />
    </Suspense>
  ) : null;

  return (
    <header className="sticky top-0 z-20 border-b border-border bg-background/88 backdrop-blur-xl">
      <HeaderBar
        account={account}
        signedIn={signedIn}
        showSignIn={showSignIn}
      />
    </header>
  );
}

function HeaderBar({
  account,
  signedIn,
  showSignIn,
}: {
  account: ReactNode;
  signedIn: boolean;
  showSignIn: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex min-h-16 w-full max-w-5xl flex-wrap items-center gap-3 px-4 py-3">
      <a
        href="/"
        className="order-1 flex min-w-0 items-center gap-2.5 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring pointer-coarse:min-h-11"
      >
        <span className="grid size-9 shrink-0 place-items-center rounded-2xl border border-primary/40 bg-primary/12 text-primary shadow-glow-sm">
          <Flame className="size-4" aria-hidden="true" />
        </span>
        <span className="truncate text-lg font-semibold tracking-tight">
          {t("app.name")}
        </span>
      </a>
      {/* The middle of the bar. Before an account it carries the two dials;
          once there is one it carries the character they chose. On a narrow
          screen the line drops under the name, on a wide one it stands
          between the name and the way out. */}
      <div
        className={cn(
          "order-3 mx-auto flex basis-full items-center gap-2",
          "sm:order-2 sm:basis-auto",
          signedIn ? "justify-center" : "justify-between sm:justify-start",
        )}
      >
        {account}
        {signedIn ? null : (
          <>
            <LanguageMenuRow
              variant="compact"
              className="flex-1 sm:flex-none"
            />
            <ThemeMenuRow variant="compact" className="flex-1 sm:flex-none" />
          </>
        )}
      </div>
      {signedIn ? (
        <div className="order-2 ml-auto flex items-center gap-2 sm:order-3 sm:ml-0">
          <Button asChild size="sm" variant="outline">
            <Link to="/home" data-testid="landing-home">
              <House aria-hidden="true" />
              {t("action.homeShort")}
            </Link>
          </Button>
        </div>
      ) : showSignIn ? (
        <Button
          asChild
          size="sm"
          variant="outline"
          className="order-2 ml-auto sm:order-3 sm:ml-0"
        >
          <a href="/auth" data-testid="landing-sign-in">
            <LogIn aria-hidden="true" />
            {t("account.signUp")}
          </a>
        </Button>
      ) : null}
    </div>
  );
}
